import * as React from 'react';

import { branch, compose } from 'recompose';

import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import CourseraPlusOwnershipContext from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';

type PropsToInnerProvider = EligibilityCheckProps &
  PropsFromWithEnrollment &
  PropsFromCourseraPlusProductOwnerships & {
    children: React.ReactNode;
  };

type PropsToDataFetcher = {
  s12nId?: string;
  courseId?: string;
  isSpecialization: boolean;
};

const CourseraPlusOwnershipDataProviderInner = ({
  children,
  isCourseraPlusEligibleProduct,
  enrollmentAvailableChoices,
  ownsCourseraPlus,
  ownsCourseraLite,
}: PropsToInnerProvider) => {
  const canSubscribeToCPlusOrLite =
    enrollmentAvailableChoices?.canSubscribeToCourseraPlus || enrollmentAvailableChoices?.canSubscribeToCourseraLite;
  const isSubscribedToCPlusOrLite = ownsCourseraPlus || ownsCourseraLite;
  const isUserEligibleToSeeCta =
    (canSubscribeToCPlusOrLite || isSubscribedToCPlusOrLite || !user.isAuthenticatedUser()) &&
    isCourseraPlusEligibleProduct;

  return (
    <CourseraPlusOwnershipContext.Provider
      value={{
        isSubscribedToCPlusOrLite,
        isUserEligibleToSeeCta,
        isCourseraPlusEligibleProduct,
        isSubscribedToCourseraLite: ownsCourseraLite,
      }}
    >
      {children}
    </CourseraPlusOwnershipContext.Provider>
  );
};

const CourseraPlusOwnershipDataFetcher = compose<PropsToInnerProvider, PropsToDataFetcher>(
  withCourseraPlusProductEligibilityCheck<PropsToDataFetcher>(true),
  branch<PropsToDataFetcher & EligibilityCheckProps>(
    () => !inServerContext,
    compose<PropsToInnerProvider, PropsToDataFetcher & EligibilityCheckProps>(
      withEnrollment(true),
      withCourseraPlusProductOwnerships(true)
    )
  )
)(CourseraPlusOwnershipDataProviderInner);

export const CourseCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useCoursePageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="CourseCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher courseId={pageData.id} isSpecialization={false}>
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};

export const SpecializationCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useSpecializationPageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="SpecializationCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher s12nId={pageData.id} isSpecialization>
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};
